import { useNavigate } from 'react-router-dom';
import { MapPin, RefreshCw, Map } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ActiveVoucherRow from './ActiveVoucherRow';
import TableSkeleton from '../../../../../components/feedback/TableSkeleton';
import EmptyStateTable from '../../../../../components/feedback/EmptyStateTable';
import { liveColumns, liveGhostRows } from '../../../utils/trackingHelpers';

const ActiveVouchersTable = ({
  vouchers,
  activeLocationByVoucher = {},
  loading,
  expandedVoucherId,
  onToggleRow,
  guestsData,
  loadingGuests,
  onPingUser,
  onRefresh
}) => {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 overflow-hidden relative mt-8">
      <div className="flex justify-between items-center mb-6 relative z-10">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <div className="p-1.5 bg-primary/10 rounded-lg text-primary">
              <MapPin size={14} />
            </div>
            Active Tour Vouchers
          </h3>
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl transition-colors text-xs font-bold uppercase tracking-wider shadow-sm cursor-pointer active:scale-95"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="overflow-x-auto relative z-10 no-scrollbar">
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="live-skeleton"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="py-2"
            >
              <TableSkeleton columns={liveColumns} rows={4} cardWrapper={false} />
            </motion.div>
          ) : vouchers.length > 0 ? (
            <motion.div
              key="live-table"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <table className="w-full text-left border-separate border-spacing-y-0 min-w-[700px]">
                <thead>
                  <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    <th className="pb-4 pl-4">Guest & ID</th>
                    <th className="pb-2">Destination</th>
                    <th className="pb-2">Status</th>
                    <th className="pb-2 text-right pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vouchers.map((voucher) => (
                    <ActiveVoucherRow
                      key={voucher._id}
                      voucher={voucher}
                      activeSharingInfo={activeLocationByVoucher[voucher._id] || (voucher.bookingID && activeLocationByVoucher[voucher.bookingID])}
                      isExpanded={expandedVoucherId === voucher._id}
                      onToggle={onToggleRow}
                      guests={guestsData[voucher._id]}
                      loadingGuests={loadingGuests[voucher._id]}
                      onPing={onPingUser}
                    />
                  ))}
                </tbody>
              </table>
            </motion.div>
          ) : (
            <motion.div
              key="live-empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <EmptyStateTable
                title="No active tours currently on-trip"
                description="Live guest locations, logged-in traveller devices, and itinerary progress will appear here when your guests log in to their branded traveller mobile app."
                columns={liveColumns}
                rows={liveGhostRows}
                primaryAction={{
                  label: 'Create Voucher',
                  onClick: () => navigate('/create-voucher')
                }}
                renderRow={(row) => (
                  <div className={`hidden md:grid grid-cols-12 gap-4 items-center px-6 py-3.5 bg-white border-b border-slate-100 ${row.opacity || 'opacity-30'} select-none pointer-events-none`}>
                    <div className="col-span-3">
                      <p className="text-slate-800 text-sm font-bold">{row.guest}</p>
                      <p className="text-[10px] text-slate-400 font-mono tracking-tight uppercase">{row.bookingId}</p>
                    </div>
                    <div className="col-span-3">
                      <span className="text-xs text-slate-600 font-semibold">{row.destination}</span>
                      <p className="text-[10px] text-slate-400">{row.devices}</p>
                    </div>
                    <div className="col-span-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${row.statusClass}`}>
                        {row.status}
                      </span>
                    </div>
                    <div className="col-span-3 text-right">
                      <button className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-slate-600 border border-slate-200 rounded-lg bg-white shadow-2xs opacity-75">
                        <Map size={12} /> Track
                      </button>
                    </div>
                  </div>
                )}
                renderMobileRow={(row) => (
                  <div className={`bg-white border rounded-2xl p-3.5 border-slate-100 ${row.opacity || 'opacity-30'} select-none pointer-events-none`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-slate-800 text-sm font-bold">{row.guest}</h4>
                        <p className="text-[11px] text-slate-400">{row.destination}</p>
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${row.statusClass}`}>
                        {row.status}
                      </span>
                    </div>
                  </div>
                )}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ActiveVouchersTable;
